<template>
  <div class="mt-8 lg:w-full">
    <p class="text-sm text-gray-500 mb-8">
      Answer the questions below. Your responses will be shared with your team lead.
    </p>

    <div v-for="qa in agentPipeline.questions_with_answers" :key="qa.question.id">
      <QuillInput
        :model-value="answers[qa.question.id] ?? ''"
        :label="qa.question.question"
        :name="`question_${qa.question.id}`"
        @update:model-value="(value: string) => (answers[qa.question.id] = value)"
      />
    </div>

    <Button class="mt-8" :disabled="!hasAnyContent || isSaving || hasClearedExistingAnswer" @click="handleClick">
      {{ allAnswered ? 'Submit' : 'Save progress' }}
    </Button>

    <Dialog :open="showSaveProgressConfirm" @update:open="showSaveProgressConfirm = $event">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save progress?</DialogTitle>
          <DialogDescription>
            This pipeline review is incomplete. You'll need to come back and finish answering the remaining questions
            before you can submit.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose as-child>
            <Button variant="outline">Keep editing</Button>
          </DialogClose>
          <Button @click="confirmSaveProgress">Save progress</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { toast } from 'vue-sonner';
import QuillInput from '@/components/shared/QuillInput.vue';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Api } from '@/api';
import type { AgentPipelineReviewResponse } from '@/api/agent/pipeline_review';

type Props = {
  agentPipeline: AgentPipelineReviewResponse;
};

type AnswersByQuestionId = Record<number, string>;

const props = defineProps<Props>();
const emit = defineEmits<{ answered: [] }>();

const buildInitialAnswers = (data: AgentPipelineReviewResponse): AnswersByQuestionId => {
  const out: AnswersByQuestionId = {};
  data.questions_with_answers.forEach(qa => {
    out[qa.question.id] = qa.answer?.answer ?? '';
  });
  return out;
};

const answers = ref<AnswersByQuestionId>(buildInitialAnswers(props.agentPipeline));
const isSaving = ref(false);
const showSaveProgressConfirm = ref(false);

watch(
  () => props.agentPipeline.questions_with_answers,
  () => {
    answers.value = buildInitialAnswers(props.agentPipeline);
  },
  { deep: true }
);

const hasText = (value: string) => {
  if (!value || value.trim().length === 0) return false;
  return value.replace(/<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>/g, '').trim().length > 0;
};

const allAnswered = computed(() =>
  props.agentPipeline.questions_with_answers.every(qa => hasText(answers.value[qa.question.id] ?? ''))
);

const hasAnyContent = computed(() =>
  props.agentPipeline.questions_with_answers.some(qa => hasText(answers.value[qa.question.id] ?? ''))
);

const hasClearedExistingAnswer = computed(() =>
  props.agentPipeline.questions_with_answers.some(
    qa => hasText(qa.answer?.answer ?? '') && !hasText(answers.value[qa.question.id] ?? '')
  )
);

const handleClick = () => {
  if (allAnswered.value) {
    submit();
  } else {
    showSaveProgressConfirm.value = true;
  }
};

const confirmSaveProgress = () => {
  showSaveProgressConfirm.value = false;
  submit();
};

const submit = async () => {
  isSaving.value = true;

  const pending = props.agentPipeline.questions_with_answers
    .map(qa => ({ question_id: qa.question.id, answer: answers.value[qa.question.id] ?? '' }))
    .filter(entry => hasText(entry.answer));

  const response = await Api.AgentPipelineReview.upsertAnswers(pending);

  isSaving.value = false;

  if (response.status === 'success') {
    toast.success('Answers saved', { description: 'Thanks for submitting your pipeline review answers.' });
    emit('answered');
  } else {
    toast.error('Pipeline Review Error', { description: response.messages.join(', ') });
  }
};
</script>
